import Phaser from 'phaser'
import { COLORS, Difficulty, PEARL_COLORS } from '../config'

interface GameOverData {
  score: number
  pearlsEarned: number
  isWin: boolean
  isNewHighScore: boolean
  difficulty: Difficulty
}

export class GameOverScene extends Phaser.Scene {
  private gameData!: GameOverData

  constructor() {
    super({ key: 'GameOverScene' })
  }

  init(data: GameOverData) {
    this.gameData = data
  }

  create() {
    const { width, height } = this.cameras.main
    
    // Semi-transparent overlay
    const overlay = this.add.graphics()
    overlay.fillStyle(COLORS.darkNavy, 0.9)
    overlay.fillRect(0, 0, width, height)
    
    // Animated bubbles/pearls in background
    this.createBackgroundEffects()
    
    // Modal container
    const modalWidth = 360
    const modalHeight = 420
    const modalX = (width - modalWidth) / 2
    const modalY = (height - modalHeight) / 2
    
    // Modal background
    const modal = this.add.graphics()
    modal.fillStyle(COLORS.mediumTeal, 0.95)
    modal.fillRoundedRect(modalX, modalY, modalWidth, modalHeight, 20)
    modal.lineStyle(4, COLORS.lightTeal)
    modal.strokeRoundedRect(modalX, modalY, modalWidth, modalHeight, 20)
    
    // Title based on win/lose
    let title: string
    let titleColor: string
    
    if (this.gameData.isWin) {
      title = this.gameData.isNewHighScore ? '🌟 Amazing! 🌟' : '✨ Wonderful! ✨'
      titleColor = '#F4A460'
    } else {
      title = this.gameData.isNewHighScore ? '🌟 New Record! 🌟' : 'Nice Try!'
      titleColor = this.gameData.isNewHighScore ? '#F4A460' : '#B8F0E8'
    }
    
    const titleText = this.add.text(width / 2, modalY + 45, title, {
      fontFamily: 'Georgia, serif',
      fontSize: '32px',
      color: titleColor,
      stroke: '#1E3A5F',
      strokeThickness: 3
    }).setOrigin(0.5)
    
    // Animate title if special
    if (this.gameData.isNewHighScore || this.gameData.isWin) {
      this.tweens.add({
        targets: titleText,
        scale: { from: 1, to: 1.1 },
        duration: 500,
        yoyo: true,
        repeat: -1
      })
    }
    
    // Win/Lose message
    const statusMessage = this.gameData.isWin 
      ? 'You cleared all the pearls!' 
      : 'The pearls reached the danger line!'
    
    this.add.text(width / 2, modalY + 90, statusMessage, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      color: '#98E8DC'
    }).setOrigin(0.5)
    
    // Score display
    this.add.text(width / 2, modalY + 130, 'Score', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#98E8DC'
    }).setOrigin(0.5)
    
    const scoreText = this.add.text(width / 2, modalY + 165, '0', {
      fontFamily: 'Georgia, serif',
      fontSize: '48px',
      color: '#FF9966',
      fontStyle: 'bold'
    }).setOrigin(0.5)
    
    // Animate score counting up
    this.tweens.addCounter({
      from: 0,
      to: this.gameData.score,
      duration: 1000,
      ease: 'Cubic.easeOut',
      onUpdate: (tween) => {
        const value = tween.getValue()
        if (value !== null) {
          scoreText.setText(Math.floor(value).toString())
        }
      }
    })
    
    // Pearls earned
    const pearlBg = this.add.graphics()
    pearlBg.fillStyle(COLORS.darkNavy, 0.5)
    pearlBg.fillRoundedRect(width / 2 - 90, modalY + 205, 180, 55, 12)
    
    this.add.text(width / 2 - 55, modalY + 232, '🐚', {
      fontSize: '24px'
    }).setOrigin(0, 0.5)
    
    const pearlsText = this.add.text(width / 2 + 10, modalY + 232, `+${this.gameData.pearlsEarned}`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '28px',
      color: '#5DD3C3',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5)
    
    // Animate pearl counter
    this.tweens.addCounter({
      from: 0,
      to: this.gameData.pearlsEarned,
      duration: 800,
      delay: 500,
      ease: 'Cubic.easeOut',
      onUpdate: (tween) => {
        const value = tween.getValue()
        if (value !== null) {
          pearlsText.setText(`+${Math.floor(value)}`)
        }
      }
    })
    
    // High score celebration
    if (this.gameData.isNewHighScore) {
      this.add.text(width / 2, modalY + 285, '⭐ NEW HIGH SCORE! ⭐', {
        fontFamily: 'Arial, sans-serif',
        fontSize: '18px',
        color: '#F4A460',
        fontStyle: 'bold'
      }).setOrigin(0.5)
    }
    
    // Play Again button
    const playAgainY = this.gameData.isNewHighScore ? modalY + 330 : modalY + 300
    this.createButton(
      width / 2,
      playAgainY,
      'Play Again',
      COLORS.brightCoral,
      () => this.scene.start('GameScene')
    )
    
    // Back to Menu button
    this.createButton(
      width / 2,
      playAgainY + 55,
      'Back to Menu',
      COLORS.mediumPurple,
      () => this.scene.start('MenuScene'),
      true
    )
    
    // High score display at bottom
    const highScore = this.registry.get('highScore') || this.gameData.score
    this.add.text(width / 2, height - 30, `🏆 Best: ${highScore}`, {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      color: '#98E8DC'
    }).setOrigin(0.5)
    
    // Space to restart
    this.input.keyboard?.once('keydown-SPACE', () => {
      this.scene.start('GameScene')
    })
  }

  createButton(
    x: number,
    y: number,
    text: string,
    color: number,
    callback: () => void,
    secondary: boolean = false
  ) {
    const buttonWidth = secondary ? 140 : 180
    const buttonHeight = secondary ? 36 : 46
    
    const container = this.add.container(x, y)
    
    const bg = this.add.graphics()
    bg.fillStyle(color)
    bg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 12)
    
    const buttonText = this.add.text(0, 0, text, {
      fontFamily: 'Arial, sans-serif',
      fontSize: secondary ? '16px' : '20px',
      color: '#FFFFFF',
      fontStyle: 'bold'
    }).setOrigin(0.5)
    
    container.add([bg, buttonText])
    container.setSize(buttonWidth, buttonHeight)
    container.setInteractive({ useHandCursor: true })
    
    container.on('pointerover', () => {
      bg.clear()
      const rgb = Phaser.Display.Color.IntegerToRGB(color)
      const lighterColor = Phaser.Display.Color.GetColor(
        Math.min(255, rgb.r + 30),
        Math.min(255, rgb.g + 30),
        Math.min(255, rgb.b + 30)
      )
      bg.fillStyle(lighterColor)
      bg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 12)
      container.setScale(1.05)
    })
    
    container.on('pointerout', () => {
      bg.clear()
      bg.fillStyle(color)
      bg.fillRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 12)
      container.setScale(1)
    })
    
    container.on('pointerdown', () => {
      container.setScale(0.95)
    })
    
    container.on('pointerup', callback)
  }

  createBackgroundEffects() {
    const { width, height } = this.cameras.main
    
    // Floating bubbles
    for (let i = 0; i < 8; i++) {
      const x = Math.random() * width
      const y = height + Math.random() * 50
      const size = 4 + Math.random() * 8
      
      const bubble = this.add.circle(x, y, size, COLORS.iceAqua, 0.2)
      
      this.tweens.add({
        targets: bubble,
        y: -20,
        x: bubble.x + (Math.random() - 0.5) * 60,
        duration: 3000 + Math.random() * 3000,
        repeat: -1,
        delay: Math.random() * 2000,
        onRepeat: () => {
          bubble.x = Math.random() * width
          bubble.y = height + 20
        }
      })
    }
    
    // Floating pearls for win
    if (this.gameData.isWin) {
      for (let i = 0; i < 6; i++) {
        const x = Math.random() * width
        const y = height + Math.random() * 100
        const colorIndex = i % PEARL_COLORS.length
        
        const pearl = this.add.sprite(x, y, `pearl_${PEARL_COLORS[colorIndex].name}`)
        pearl.setAlpha(0.5)
        pearl.setScale(0.7)
        
        this.tweens.add({
          targets: pearl,
          y: -50,
          x: pearl.x + (Math.random() - 0.5) * 100,
          angle: Phaser.Math.Between(-180, 180),
          duration: 4000 + Math.random() * 3000,
          repeat: -1,
          delay: Math.random() * 2000,
          onRepeat: () => {
            pearl.x = Math.random() * width
            pearl.y = height + 50
          }
        })
      }
    }
  }
}
